import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { IProductDetail } from "../../interfaces/shopify";
import {
  buildLayeredBackground,
  cleanColorName,
  isUrl,
  sortSizes,
  toColor,
} from "../../utils/funtions";
import { colorDoubleMap } from "../../helpers/Colors";
import {
  ArrowRightFromBracket,
  Cart,
  DirectionLeftRight,
  GridOrder,
} from "../../icons";
import { InputQtyGrid } from "./components/InputQtyGrid";
import { useCheckoutStore } from "../../store/checkoutStore";
import { useNavigate } from "react-router-dom";
import { TableGridCell } from "./components/TableGridCell";

/* =========================================================
   Helpers
   ========================================================= */

const normalize = (s?: string) =>
  String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const aliasMap: Record<
  string,
  "Color" | "Size" | "Width" | "Shank" | "Title" | undefined
> = {
  // Color
  color: "Color",
  colour: "Color",
  "color name": "Color",
  // Size
  size: "Size",
  "size us": "Size",
  "size eu": "Size",
  "shoe size": "Size",
  // Width
  width: "Width",
  "width (w)": "Width",
  "foot width": "Width",
  widths: "Width",
  // Shank
  shank: "Shank",
  // Title
  title: "Title",
};

const detectOptionNames = (edges: any[]) => {
  const names = new Set<string>();
  edges.forEach((e) =>
    (e?.node?.selectedOptions ?? []).forEach((o: any) => {
      if (o?.name) names.add(o.name);
    })
  );

  // evita iterar Set con spread por TS target bajos
  const all = Array.from(names);

  const canonicalOriginalByCanonical: Record<string, string> = {};
  all.forEach((n) => {
    const norm = normalize(n);
    const mapped = aliasMap[norm];
    if (mapped) {
      canonicalOriginalByCanonical[mapped] = n; // nombre original presente en el producto
    } else {
      // si no hay alias, queda con su mismo nombre
      canonicalOriginalByCanonical[n] = n;
    }
  });

  return {
    // nombres ORIGINALES presentes en Shopify para cada canónico (si existe)
    colorName: canonicalOriginalByCanonical["Color"],
    sizeName: canonicalOriginalByCanonical["Size"],
    widthName: canonicalOriginalByCanonical["Width"],
    shankName: canonicalOriginalByCanonical["Shank"],
    titleName: canonicalOriginalByCanonical["Title"],
    allOriginal: all, // lista de nombres originales tal como vienen
  };
};

const getOptionsFor = (edges: any[], originalName?: string) =>
  !originalName
    ? []
    : Array.from(
        new Set(
          edges.flatMap(
            (edge: any) =>
              edge?.node?.selectedOptions
                ?.filter((opt: any) => opt?.name === originalName)
                ?.map((opt: any) => String(opt?.value ?? "")) ?? []
          )
        )
      );

const makeVariantIndex = (variantEdges: any[]) => {
  const makeKey = (opts: Record<string, string>) =>
    Object.entries(opts)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => `${normalize(k)}=${normalize(v)}`)
      .sort()
      .join("|");

  const idx = new Map<string, any>();

  for (const edge of variantEdges) {
    const node = edge?.node;
    if (!node) continue;
    const opts = Object.fromEntries(
      (node.selectedOptions ?? []).map((o: any) => [
        o?.name ?? "",
        o?.value ?? "",
      ])
    ) as Record<string, string>;
    idx.set(makeKey(opts), node);
  }

  const findVariant = (partial: Record<string, string | undefined>) => {
    const cleaned: Record<string, string> = {};
    for (const [k, v] of Object.entries(partial)) if (v) cleaned[k] = v;
    return idx.get(makeKey(cleaned));
  };

  const getAvailable = (variant: any): number => {
    if (!variant) return 0;
    const qa =
      variant.quantityAvailable ??
      variant.inventoryQuantity ??
      (variant.availableForSale === false ? 0 : undefined);
    return Number.isFinite(qa) ? Number(qa) : 0;
  };

  const getPrice = (variant: any): number => {
    const amt = variant?.price?.amount;
    const n = Number(amt);
    return Number.isFinite(n) ? n : 0;
  };

  return { findVariant, getAvailable, getPrice };
};

/* =========================================================
   Componente
   ========================================================= */

type DialogTableOrderProps = {
  data: IProductDetail | null | undefined;
  open?: boolean;
  onClose: (v: boolean) => void;
};

export const DialogTableOrder: React.FC<DialogTableOrderProps> = ({
  data,
  open,
  onClose,
}) => {
  const theme = useTheme();

  /* 1) Unificar variantes desde referencias y desde el propio producto */
  const variantEdges = useMemo(() => {
    const fromRefs =
      data?.productVariantMetafield?.references?.edges.flatMap(
        (r: any) => r?.node?.variants?.edges ?? []
      ) ?? [];
    const fromProduct = data?.variants?.edges ?? [];
    // evita duplicados por id
    const seen = new Set<string>();
    const merged: any[] = [];
    for (const e of Array.from([...fromRefs, ...fromProduct])) {
      const id = e?.node?.id;
      if (id && !seen.has(id)) {
        seen.add(id);
        merged.push(e);
      }
    }
    return merged;
  }, [data]);

  /* 2) Detectar nombres originales presentes (alias -> canónico) */
  const { colorName, sizeName, widthName, shankName, allOriginal } = useMemo(
    () => detectOptionNames(variantEdges),
    [variantEdges]
  );

  /* 3) Elegir ejes dinámicamente (preferencia Color×Size; si no, Width×Size; si no, 2 primeras opciones) */
  const { axisPrimaryName, axisSecondaryName } = useMemo(() => {
    // candidatos presentes
    const candidates = new Set(allOriginal);

    const c = colorName && candidates.has(colorName) ? colorName : undefined;
    const s = sizeName && candidates.has(sizeName) ? sizeName : undefined;
    const w = widthName && candidates.has(widthName) ? widthName : undefined;

    if (c && s) {
      return { axisPrimaryName: c, axisSecondaryName: s };
    }
    if (!c && w && s) {
      return { axisPrimaryName: w, axisSecondaryName: s };
    }

    // fallback: toma las dos primeras opciones disponibles
    const list = Array.from(candidates);
    const primary = list[0];
    const secondary = list.find((x) => x !== primary);
    return { axisPrimaryName: primary, axisSecondaryName: secondary };
  }, [allOriginal, colorName, sizeName, widthName]);

  /* 4) Valores para filas/columnas y extras (terciarios) */
  const rowValues = useMemo(
    () => getOptionsFor(variantEdges, axisPrimaryName),
    [variantEdges, axisPrimaryName]
  );
  const colValues = useMemo(
    () => getOptionsFor(variantEdges, axisSecondaryName),
    [variantEdges, axisSecondaryName]
  );

  // Extras: cualquier opción original que NO sea uno de los ejes
  const extraOptionNames = useMemo(
    () =>
      Array.from(allOriginal).filter(
        (n) => n !== axisPrimaryName && n !== axisSecondaryName
      ),
    [allOriginal, axisPrimaryName, axisSecondaryName]
  );

  // Mapa de valores posibles por extra
  const extraOptionValuesMap = useMemo(() => {
    const m = new Map<string, string[]>();
    extraOptionNames.forEach((n) => {
      m.set(n, getOptionsFor(variantEdges, n));
    });
    return m;
  }, [variantEdges, extraOptionNames]);

  // Estado de selección de extras (Width, Shank, etc.)
  const [selectedExtra, setSelectedExtra] = useState<
    Record<string, string | undefined>
  >({});

  // Inicializar valores por defecto para cada extra
  useEffect(() => {
    const next: Record<string, string | undefined> = { ...selectedExtra };
    extraOptionNames.forEach((n) => {
      const vals = extraOptionValuesMap.get(n) ?? [];
      if (vals.length && !next[n]) next[n] = vals[0];
      // si no hay valores, lo dejamos indefinido
    });
    setSelectedExtra(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraOptionNames.join("|")]); // usar join para dependencia simple

  /* 5) Index, disponibilidad y precio */
  const { findVariant, getAvailable, getPrice } = useMemo(
    () => makeVariantIndex(variantEdges),
    [variantEdges]
  );

  /* 6) Store */
  const { addVariantGrid, variants, removeVariant } = useCheckoutStore();

  const [qtyByVariant, setQtyByVariant] = useState<Record<string, number>>({});
  const handleQtyChange = (
    variantId: string,
    value: number,
    available?: number,
    price?: number,
    qty?: number
  ) => {
    setQtyByVariant((prev) => ({
      ...prev,
      [variantId]: Number.isFinite(value) && value >= 0 ? value : 0,
    }));

    addVariantGrid({
      variants: variantId,
      available,
      amount: price,
      qty: qty,
    });
  };

  /* 7) Navegación */
  const navigate = useNavigate();
  const onCheckout = () => navigate("/checkout");

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => onClose(false)}
      disableScrollLock={false}
      slotProps={{
        paper: {
          style: {
            minHeight: "100%",
            backgroundColor: theme.palette.background.default,
          },
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        position="sticky"
        top={0}
        padding="12px 15px"
      >
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <GridOrder
            sx={{ width: 18, height: 18 }}
            htmlColor={theme.palette.secondary[100]}
          />
          <Typography
            sx={{ color: theme.palette.secondary[700] }}
            lineHeight={0.8}
            variant="h5"
          >
            Batch Order
          </Typography>
        </Box>

        <Box display="flex" justifyContent="flex-start">
          <IconButton sx={{ position: "relative" }} onClick={onCheckout}>
            <Box
              sx={{
                position: "absolute",
                right: -2,
                padding: "10px 6px",
                top: -2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "100vmax",
                backgroundColor: theme.palette.primary[500],
              }}
            >
              <Typography
                fontSize={10}
                fontWeight={600}
                lineHeight={0}
                color={theme.palette.common.white}
              >
                {variants.reduce((acc, q) => acc + q.qty, 0)}
              </Typography>
            </Box>

            <Cart sx={{ width: 22, height: 22 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Tabla */}
      <TableContainer
        sx={{
          scrollbarColor: `${theme.palette.customColor?.light} ${theme.palette.common.white}`,
          height: "100svh",
        }}
        elevation={0}
        component={Paper}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                "&:last-child td, &:last-child th": {
                  border: `solid 1px ${theme.palette.brand?.main}`,
                },
              }}
            >
              <TableCell
                colSpan={1}
                sx={{
                  position: "sticky",
                  backgroundColor: theme.palette.background.paper,
                  backdropFilter: "saturate(50%) blur(5px)",
                  left: 0,
                  textWrap: "nowrap",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  zIndex: 999,
                  color: theme.palette.secondary[600],
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography fontWeight={700}>
                    {axisPrimaryName || "Option"}
                  </Typography>
                  <DirectionLeftRight />
                  <Typography fontWeight={700}>
                    {axisSecondaryName || "Size"}
                  </Typography>
                </Box>
              </TableCell>

              {(colValues.length ? sortSizes(colValues) : [""]).map(
                (size, idx) => (
                  <TableCell
                    sx={{
                      textWrap: "nowrap",
                      backgroundColor: theme.palette.background.paper,
                      backdropFilter: "saturate(50%) blur(5px)",
                      color: theme.palette.secondary[600],
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                    align="center"
                    key={size || idx}
                  >
                    {size || "Quantity"}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              "&:last-child td, &:last-child th": {
                border: `solid 1px ${theme.palette.brand?.main}`,
              },
            }}
          >
            {rowValues.map((rowVal) => {
              const isColorRow =
                axisPrimaryName === colorName && typeof rowVal === "string";
              const name = isColorRow
                ? cleanColorName(rowVal).replace(/[\s/]+/g, "")
                : "";

              const isSlash = isColorRow && rowVal.includes("/");
              let stops: string[] = [];
              if (isSlash) {
                const arr = colorDoubleMap[name] ?? ["blue", "red"];

                stops = Array.isArray(arr) ? arr.slice(0, 3) : ["blue", "red"];
              } else {
                if (isUrl(name)) stops = [name];
                else stops = [toColor(name)];
              }
              const bg = buildLayeredBackground(stops);

              return (
                <TableRow
                  key={rowVal}
                 
                >
                  <TableCell
                    sx={{ backgroundColor: theme.palette.common.white, height: 70 }}
                  >
                    <Box display="flex" gap={2} alignItems="center">
                      {isColorRow && (
                        <Box
                          sx={{
                            boxShadow:
                              "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
                            minWidth: 35,
                            minHeight: 35,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "8px !important",
                            objectFit: "cover",
                            backgroundPosition: "center",
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                            ...(bg.backgroundImage
                              ? {
                                  backgroundImage: bg.backgroundImage,
                                }
                              : {}),
                            ...(bg.backgroundColor
                              ? {
                                  backgroundColor: bg.backgroundColor,
                                }
                              : {}),
                          }}
                          borderRadius={1}
                          boxShadow={`rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px`}
                        />
                      )}
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: theme.palette.secondary[600] }}
                      >
                        {rowVal === "Default Title" ? data?.title : rowVal}
                      </Typography>
                    </Box>
                  </TableCell>

                  {(colValues.length ? colValues : [""]).map((colVal, idx) => {
                    // Construye criterios de búsqueda: eje1/eje2 + extras seleccionados
                    const criteria: Record<string, string | undefined> = {
                      ...(axisPrimaryName ? { [axisPrimaryName]: rowVal } : {}),
                      ...(axisSecondaryName
                        ? { [axisSecondaryName]: colVal || undefined }
                        : {}),
                    };

                    // añade todas las selecciones extra
                    Object.entries(selectedExtra).forEach(([k, v]) => {
                      if (v) criteria[k] = v;
                    });

                    const variant = findVariant(criteria);

                    const vId = variant?.id ?? "";
                    const selectedIds = variants.map((v) => v.variants);
                    const selectedQty = variants
                      .filter((v) => v.variants.includes(vId))
                      .map((v) => v.qty);
                    const value = vId ? qtyByVariant[vId] ?? selectedQty[0] : 0;

                    const availableQty = getAvailable(variant);
                    const priceNumber = getPrice(variant);
                    const priceText = priceNumber
                      ? `$${priceNumber.toFixed(2)}`
                      : "";

                    return (
                      <TableCell
                        sx={{
                          padding: availableQty <= 0 ? 0 : 5,
                          minWidth: 150,
                          height: 70,
                          position: "relative",
                          "&:hover": {
                            cursor: "pointer",
                            backgroundColor: availableQty > 0 ? `${theme.palette.customColor?.dark} !important` : `"#FFF0ED`,
                          },
                          backgroundColor: selectedIds.includes(vId)
                            ? "#FFF0ED"
                            : "",
                        }}
                        key={`${rowVal}-${colVal || idx}`}
                        align="center"
                      >
                        {availableQty > 0 ? (
                          <>
                            {selectedIds.includes(vId) && (
                              <TableGridCell
                                availableQty={availableQty}
                                onClick={() => {
                                  removeVariant(vId);
                                  handleQtyChange(vId, 0);
                                }}
                                selectedQty={selectedQty}
                              />
                            )}

                            <InputQtyGrid
                              availableQty={availableQty}
                              disabled={!variant}
                              value={value}
                              onValueChange={(qty) =>
                                variant &&
                                handleQtyChange(
                                  variant.id,
                                  qty,
                                  availableQty,
                                  priceNumber,
                                  qty
                                )
                              }
                              dataVariantId={variant?.id}
                            
                              dataSize={
                                axisSecondaryName === sizeName
                                  ? colVal || undefined
                                  : axisPrimaryName === sizeName
                                  ? rowVal || undefined
                                  : undefined
                              }
                              dataWidth={
                                axisSecondaryName === widthName
                                  ? colVal || undefined
                                  : axisPrimaryName === widthName
                                  ? rowVal || undefined
                                  : selectedExtra[widthName ?? ""] 
                              }
                              dataSelectedShank={
                                selectedExtra[shankName ?? ""] 
                              }
                            />

                            <Box display="flex" justifyContent="center">
                              <Typography
                                fontWeight={600}
                                fontSize={10}
                                sx={{
                                  userSelect: "none",
                                  color: theme.palette.secondary[400],
                                  position: "absolute",
                                  bottom: 2,
                                }}
                              >
                                {priceText}
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Box width="100%"  height="100%" bgcolor={theme.palette.brand?.main}></Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer: Selectores dinámicos para TODAS las opciones extra (Width, Shank, etc.) */}
      <Box
        sx={{
          position: "sticky",
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          justifyContent:
            extraOptionNames.length > 0 ? "space-between" : "flex-end",
          alignItems: "center",
          width: "100%",
          bottom: 0,
          paddingBlock: 4,
        }}
      >
        {/* Un bloque por cada opción extra */}
        {extraOptionNames.map((optName) => {
          const values = extraOptionValuesMap.get(optName) ?? [];
          if (!values.length) return null;
          const selectedVal = selectedExtra[optName];

          // etiqueta bonita si coincide con alias conocidos
          const niceLabel =
            (optName === (shankName ?? "__") && "Shank") ||
            (optName === (widthName ?? "__") && "Width") ||
            (optName === (colorName ?? "__") && "Color") ||
            (optName === (sizeName ?? "__") && "Size") ||
            optName;

          return (
            <Box
              key={optName}
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              gap={5}
              marginInline={3}
            >
              <Typography
                minWidth={50}
                fontWeight={700}
                textTransform="uppercase"
                sx={{ color: theme.palette.secondary[500] }}
                variant="body1"
              >
                {niceLabel}
              </Typography>
              <Divider orientation="vertical" variant="fullWidth" flexItem />
              <Box display="flex" alignItems="center" gap={2}>
                {values.map((val) => {
                  const selected = val === selectedVal;
                  return (
                    <Button
                      size="small"
                      disableElevation
                      key={val}
                      color="secondary"
                      onClick={() =>
                        setSelectedExtra((prev) => ({
                          ...prev,
                          [optName]: val,
                        }))
                      }
                      sx={{
                        fontSize: "13px !important",
                        fontWeight: "600 !important",
                        borderRadius: "5px !important",
                        color: selected
                          ? theme.palette.secondary[500]
                          : theme.palette.secondary[100],
                        backgroundColor: selected
                          ? theme.palette.customColor?.main
                          : "transparent",
                        "&:hover": {
                          boxShadow: `0px 0px 0px 2px ${theme.palette.secondary[100]}`,
                        },
                        boxShadow: selected
                          ? `0px 0px 0px 2px ${theme.palette.customColor?.light}`
                          : `0px 0px 0px 1px ${theme.palette.customColor?.light}`,
                      }}
                    >
                      {val}
                    </Button>
                  );
                })}
              </Box>
            </Box>
          );
        })}

        <Button
          endIcon={
            <ArrowRightFromBracket
              sx={{ width: 18, height: 18 }}
              htmlColor={theme.palette.primary[700]}
            />
          }
          onClick={() => onClose(false)}
          disableElevation
          sx={{
            padding: { xs: "10px", md: "9px 12px" },
            color: theme.palette.secondary[600],
            "&:hover": { backgroundColor: theme.palette.primary[300] },
            width: { xs: "100%", md: "auto" },
            marginInline: 3,
          }}
          variant="contained"
        >
          Close
        </Button>
      </Box>
    </Drawer>
  );
};
